import React, { useState, useEffect } from 'react';
import { Show } from '../types';
import { api } from '../services/api';
import MusicPlayer from './AudioPlayer';
import Stars from './Stars';
import LogoProcessor from './LogoProcessor';
import DefaultShowArt from './DefaultShowArt';
import BookingForm from './BookingForm';
import { formatShowDate, isUpcoming, sortShowsByDate } from '../utils/dateUtils';

const PublicSite: React.FC = () => {
  const [shows, setShows] = useState<Show[]>([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentHeroImage, setCurrentHeroImage] = useState(0);
  const [currentShowPage, setCurrentShowPage] = useState(0);
  const showsPerPage = 5;
  const [activeSection, setActiveSection] = useState('home');
  const [expandedFlyer, setExpandedFlyer] = useState<string | null>(null);
  const [expandedShow, setExpandedShow] = useState<Show | null>(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showSubscribePopup, setShowSubscribePopup] = useState(false);
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);

  // Band configuration photos
  const bandPhotos: Record<string, string> = {
    'duet': '/images/duet-photo.jpg',
    'trio': '/images/IMG_6944.jpeg',
    'full': '/images/full-band-photo.jpg'
  };

  const heroImages = [
    '/images/ken.jpg',
    '/images/atw.jpeg',
    '/images/hero1.jpg'
  ];

  useEffect(() => {
    loadShows();
    
    // Check if user came to /booking route
    if (window.location.pathname === '/booking') {
      setShowBookingForm(true);
      // Change URL back to home without page reload
      window.history.replaceState({}, '', '/');
    }
    
    // Check localStorage for last submission time
    const storedTime = localStorage.getItem('lastNewsletterSubmission');
    if (storedTime) {
      setLastSubmissionTime(parseInt(storedTime));
    }
    
    // Add scroll listener for active section
    const handleScroll = () => {
      const sections = ['home', 'events', 'music', 'about', 'contact'];
      const navHeight = 136;
      const scrollPosition = window.scrollY + navHeight + 50; // Add buffer for detection
      
      // Check if scrolled to bottom
      const scrolledToBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 100;
      if (scrolledToBottom) {
        setActiveSection('contact');
        return;
      }
      
      // Check each section from bottom to top
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          if (scrollPosition >= offsetTop) {
            setActiveSection(section);
            break;
          }
        }
      }
      
      // Special case for home when scrolled to top
      if (window.scrollY < 100) {
        setActiveSection('home');
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  useEffect(() => {
    // Rotate hero images every 8 seconds
    const interval = setInterval(() => {
      setCurrentHeroImage((prev) => (prev + 1) % heroImages.length);
    }, 8000);
    
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const loadShows = async () => {
    try {
      const data = await api.getShows();
      const upcomingShows = sortShowsByDate(
        data.filter(show => !show.isCancelled && isUpcoming(show.date))
      );
      setShows(upcomingShows);
    } catch (error) {
      console.error('Failed to load shows:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navHeight = 136; // Height of the fixed navigation
      
      // Set active section immediately
      setActiveSection(sectionId);
      
      if (sectionId === 'home') {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - navHeight + 10; // Small positive offset to ensure section header is visible
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
      
      setMobileMenuOpen(false);
    }
  };

  const bandMembers = [
    { 
      name: 'Ken Marut', 
      role: 'Banjo/Guitar',
      position: { x: 10, textSide: 'left', width: 28 }
    },
    { 
      name: 'Eddie Collins', 
      role: 'Bass/Banjo',
      position: { x: 32, textSide: 'left', width: 26 }
    },
    { 
      name: 'Loren Gunn', 
      role: 'Mando/Vox',
      position: { x: 50, textSide: 'right', width: 25 }
    },
    { 
      name: 'Roam Gunn', 
      role: 'Guitar/Vox',
      position: { x: 67, textSide: 'right', width: 28 }
    },
    { 
      name: 'Jerry Hill', 
      role: 'Fiddle',
      position: { x: 90, textSide: 'right', width: 35 }
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white relative overflow-x-hidden pb-16">
      {/* Animated Stars Background */}
      <Stars />
      
      {/* Space gradient overlay */}
      <div className="fixed inset-0 galaxy-gradient opacity-50 pointer-events-none z-0"></div>
      
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-black/80 backdrop-blur-md z-50 border-b border-blue-900/30 py-2">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <div onClick={() => scrollToSection('home')} className="cursor-pointer">
                <LogoProcessor
                  src="/images/Across the Way.png" 
                  alt="Across The Way" 
                  className={`h-24 w-24 md:h-32 md:w-32 logo-glow ${activeSection === 'home' ? 'logo-active' : ''}`}
                />
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-4">
              <button onClick={() => scrollToSection('events')} className={`nav-glow px-6 py-2 text-2xl font-light tracking-wider transition-all text-white ${activeSection === 'events' ? 'nav-active' : ''}`}>Events</button>
              <button onClick={() => scrollToSection('music')} className={`nav-glow px-6 py-2 text-2xl font-light tracking-wider transition-all text-white ${activeSection === 'music' ? 'nav-active' : ''}`}>Music</button>
              <button onClick={() => scrollToSection('about')} className={`nav-glow px-6 py-2 text-2xl font-light tracking-wider transition-all text-white ${activeSection === 'about' ? 'nav-active' : ''}`}>About</button>
              <button onClick={() => scrollToSection('contact')} className={`nav-glow px-6 py-2 text-2xl font-light tracking-wider transition-all text-white ${activeSection === 'contact' ? 'nav-active' : ''}`}>Contact</button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-300 hover:text-white"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-blue-900/30">
              <div className="flex flex-col space-y-1">
                <button onClick={() => scrollToSection('events')} className={`nav-link-mobile text-left px-4 py-3 ${activeSection === 'events' ? 'text-blue-400' : ''}`}>Events</button>
                <button onClick={() => scrollToSection('music')} className={`nav-link-mobile text-left px-4 py-3 ${activeSection === 'music' ? 'text-blue-400' : ''}`}>Music</button>
                <button onClick={() => scrollToSection('about')} className={`nav-link-mobile text-left px-4 py-3 ${activeSection === 'about' ? 'text-blue-400' : ''}`}>About</button>
                <button onClick={() => scrollToSection('contact')} className={`nav-link-mobile text-left px-4 py-3 ${activeSection === 'contact' ? 'text-blue-400' : ''}`}>Contact</button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ marginTop: '136px' }}>
        <div 
          className="absolute inset-0 bg-cover bg-center transition-all duration-1000"
          style={{
            backgroundImage: `url(${heroImages[currentHeroImage]})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center 30%',
          }}
        >
          {/* Space-themed gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/90"></div>
          <div className="absolute inset-0 milky-way"></div>
        </div>
        <div className="relative z-10 text-center px-4 animate-fadeInUp">
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-4 tracking-wider text-shadow uppercase" style={{ fontFamily: 'Oswald, sans-serif' }}>ACROSS THE WAY</h1>
          <p className="text-lg md:text-xl lg:text-2xl mb-8 text-enhanced-gray-200 text-shadow font-light tracking-widest uppercase">Austin-Based Bluegrass & Country</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => scrollToSection('events')}
              className="btn-primary"
            >
              Events
            </button>
            <button 
              onClick={() => scrollToSection('music')}
              className="btn-secondary"
            >
              MUSIC
            </button>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Events Section */}
      <section id="events" className="py-16 px-4 relative z-10" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,4,40,0.8) 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-center mb-12 uppercase text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>Events</h2>
          
          {loading ? (
            <div className="space-y-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="skeleton h-40 md:h-48 rounded-lg"></div>
              ))}
            </div>
          ) : shows.length === 0 ? (
            <div className="text-center">
              <p className="text-enhanced-gray-200 mb-8 text-lg">No upcoming events at the moment.</p>
              <p className="text-enhanced-gray-300">Check back soon or contact us for booking!</p>
            </div>
          ) : (
            <>
              <div className="grid gap-6 md:gap-8">
                {shows
                  .slice(currentShowPage * showsPerPage, (currentShowPage + 1) * showsPerPage)
                  .map(show => (
                  <div key={show.id} className="group bg-gray-900/80 md:bg-gray-900/90 border border-blue-900/40 backdrop-blur-sm show-card-enhanced rounded-lg relative overflow-hidden cursor-pointer mb-6 md:mb-6 shadow-lg" onClick={() => setExpandedShow(show)}>
                    {/* Simplified hover effect */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5"></div>
                    </div>
                    
                    {/* Desktop Layout - unchanged */}
                    <div className="hidden md:block relative z-10 p-8">
                      <div className="flex items-start justify-between">
                        <div className="flex mr-6 flex-shrink-0 gap-4">
                          {/* Event/Venue Photo - Only show if flyer exists */}
                          {show.flyerUrl && (
                            <div>
                              <div
                                className="relative w-32 h-32 md:w-40 md:h-40 rounded-lg overflow-hidden cursor-pointer border-2 border-blue-900/30 hover:border-blue-700/50 transition-all group"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedFlyer(show.flyerUrl!);
                                }}
                              >
                                <img
                                  src={show.flyerUrl}
                                  alt={`${show.venue.name} flyer`}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          )}
                          {/* Band Configuration Photo */}
                          <div>
                            {show.bandConfig && bandPhotos[show.bandConfig] ? (
                              <img
                                src={bandPhotos[show.bandConfig]}
                                alt={`${show.bandConfig === 'duet' ? 'Duet' : show.bandConfig === 'trio' ? 'Trio' : 'Full Band'}`}
                                className="w-32 h-32 md:w-40 md:h-40 object-cover rounded-lg border-2 border-purple-900/30"
                              />
                            ) : show.eventType?.includes('full') ? (
                              <img
                                src={bandPhotos['full']}
                                alt="Full Band"
                                className="w-32 h-32 md:w-40 md:h-40 object-cover rounded-lg border-2 border-purple-900/30"
                              />
                            ) : (
                              <div className="w-32 h-32 md:w-40 md:h-40 rounded-lg border-2 border-purple-900/30 bg-gray-800/50 flex items-center justify-center">
                                <p className="text-gray-400 text-sm font-bold text-center" style={{ fontFamily: 'Oswald, sans-serif' }}>
                                  {show.bandConfig === 'duet' ? 'DUET' :
                                   show.bandConfig === 'trio' ? 'TRIO' :
                                   'FULL\nBAND'}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between">
                          <div className="flex-1">
                            <div className="mb-3">
                              <span className="event-date-badge">
                                {formatShowDate(show.date, 'EEEE, MMMM d, yyyy')}
                              </span>
                            </div>
                            <p className="text-enhanced-gray-200 font-semibold">
                              {formatShowDate(show.date, 'MMMM d')} @ {show.startTime}
                            </p>
                            {show.notes && (
                              <p className="text-enhanced-gray-300 mt-2">{show.notes}</p>
                            )}
                          </div>
                            <div className="text-right ml-8">
                            <h3 className="venue-name font-bold mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>{show.venue.name}</h3>
                            {!show.isPrivate && show.venue.address.street && (
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                  `${show.venue.address.street}, ${show.venue.address.city}, ${show.venue.address.state} ${show.venue.address.zip}`
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block text-enhanced-gray-300 hover:text-blue-400 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <p className="flex items-center gap-1 justify-end">
                                  <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  {show.venue.address.street}
                                </p>
                                <p className="text-right">
                                  {show.venue.address.city}, {show.venue.address.state} {show.venue.address.zip}
                                </p>
                              </a>
                            )}
                            {/* Event Type Badge */}
                            {(show.eventType === 'private' || show.isPrivate) && (
                              <p className="text-gray-600 font-semibold mt-1">Private Event</p>
                            )}
                            {show.eventType !== 'private' && !show.isPrivate && (
                              <p className="mt-2">
                                <span className="free-event-badge">
                                  {(show.eventType === 'internet' || show.venue?.name?.toLowerCase().includes('up late')) ? 'Online' :
                                   (show.eventType === 'ticketed' || show.isTicketed) ? 'Ticketed Event' :
                                   'Free Event'}
                                </span>
                              </p>
                            )}
                            {show.eventType === 'ticketed' && show.ticketInfo && (
                              <div className="mt-3">
                                <a 
                                  href={show.ticketInfo.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="btn-primary text-sm"
                                >
                                  <span className="mr-2">Get Tickets</span>
                                  <span>{show.ticketInfo.price}</span>
                                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                              </div>
                            )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Mobile Layout - 3x2 Grid Design */}
                    <div className="md:hidden relative z-10 bg-gray-800/50 p-4">
                      <div className="grid grid-cols-2 gap-3">
                        {/* Row 1 Col 1: Show Art - Only show if flyer exists */}
                        {show.flyerUrl ? (
                          <div>
                            <div
                              className="relative w-full aspect-square rounded-lg overflow-hidden cursor-pointer border-2 border-blue-900/30 shadow-lg"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedFlyer(show.flyerUrl!);
                              }}
                            >
                              <img
                                src={show.flyerUrl}
                                alt={`${show.venue.name} flyer`}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 active:opacity-100 transition-opacity flex items-center justify-center">
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div></div>
                        )}
                        
                        {/* Row 1 Col 2: Venue Name & Address */}
                        <div className="text-right">
                          <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>
                            {show.venue.name}
                          </h3>
                          {!show.isPrivate && show.venue.address.street && (
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                `${show.venue.address.street}, ${show.venue.address.city}, ${show.venue.address.state} ${show.venue.address.zip}`
                              )}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-300 hover:text-blue-400 underline block"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-start gap-1 justify-end">
                                <svg className="w-3 h-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <div className="text-right">
                                  <div>{show.venue.address.street}</div>
                                  <div>{show.venue.address.city}, {show.venue.address.state}</div>
                                </div>
                              </div>
                            </a>
                          )}
                        </div>
                        
                        {/* Row 2 Col 1: Date & Time */}
                        <div className="flex items-center">
                          <p className="text-sm font-semibold text-blue-400">
                            {formatShowDate(show.date, 'MMMM d')} @ {show.startTime}
                          </p>
                        </div>
                        
                        {/* Row 2 Col 2: Status/Tickets */}
                        <div className="flex items-center justify-end">
                          {show.isPrivate && (
                            <span className="inline-block px-3 py-1 bg-gray-700 text-gray-300 text-sm rounded-full">Private</span>
                          )}
                          {show.eventType !== 'ticketed' && !show.isPrivate && (
                            <span className="inline-block px-3 py-1 bg-green-900/50 text-green-400 text-sm rounded-full border border-green-800/50">Free</span>
                          )}
                          {show.ticketInfo && (
                            <a 
                              href={show.ticketInfo.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-full hover:bg-blue-700 transition"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span>Tickets</span>
                              <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          )}
                        </div>
                        
                        {/* Row 3: Notes spanning full width */}
                        {show.notes && (
                          <div className="col-span-2 pt-3 border-t border-gray-700/50">
                            <p className="text-sm text-gray-300 italic">{show.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination */}
              {shows.length > showsPerPage && (
                <div className="flex justify-center items-center gap-4 mt-8">
                  <button
                    onClick={() => setCurrentShowPage(prev => Math.max(0, prev - 1))}
                    disabled={currentShowPage === 0}
                    className={`p-3 rounded-full transition-all ${
                      currentShowPage === 0 
                        ? 'text-gray-600 cursor-not-allowed' 
                        : 'text-white hover:bg-blue-600/20 focus:outline-none focus:ring-2 focus:ring-blue-400/50'
                    }`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  
                  <span className="text-gray-400">
                    Page {currentShowPage + 1} of {Math.ceil(shows.length / showsPerPage)}
                  </span>
                  
                  <button
                    onClick={() => setCurrentShowPage(prev => Math.min(Math.ceil(shows.length / showsPerPage) - 1, prev + 1))}
                    disabled={currentShowPage >= Math.ceil(shows.length / showsPerPage) - 1}
                    className={`p-3 rounded-full transition-all ${
                      currentShowPage >= Math.ceil(shows.length / showsPerPage) - 1
                        ? 'text-gray-600 cursor-not-allowed' 
                        : 'text-white hover:bg-blue-600/20 focus:outline-none focus:ring-2 focus:ring-blue-400/50'
                    }`}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Music & Videos Section */}
      <section id="music" className="py-20 px-4 relative z-10" style={{ background: 'linear-gradient(180deg, rgba(0,4,40,0.6) 0%, rgba(0,0,0,0.9) 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-center mb-12 uppercase text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>MUSIC</h2>

          {/* Audio Player */}
          <div className="max-w-4xl mx-auto mb-16">
            <h3 className="text-3xl font-semibold text-center mb-8">RECORDINGS</h3>
            <div className="bg-black/50 p-8 rounded-lg border border-blue-900/30 space-glow">
              <MusicPlayer />
            </div>
          </div>

          {/* Videos coming soon - removed YouTube due to copyright */}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-center mb-12 uppercase text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>About the Band</h2>
          
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-xl text-gray-300 leading-relaxed">
              Across The Way is an Austin-based five-piece Bluegrass/Country band made up of Roam and Loren Gunn and their friends: Ken Marut, Jerry Hill, and Eddie Collins. We love pickin' and singin' for fun-loving folks. Available as a full band, duo, or any combination that suits your event.
            </p>
          </div>

          {/* Interactive Band Photo */}
          <div className="relative max-w-6xl mx-auto mb-12">
            <div className="relative">
              {/* Band photo */}
              <img 
                src="/images/atw.jpeg" 
                alt="Across The Way Band" 
                className="w-full rounded-lg"
              />
              
            </div>
            
            {/* Member buttons - Desktop only */}
            <div className="hidden md:grid md:grid-cols-5 gap-4 mt-8">
              {bandMembers.map((member, index) => (
                <div
                  key={index}
                  className="p-4 rounded-lg transition-all bg-black/50 border border-blue-900/30 hover:bg-black/70 hover:border-blue-700/50 text-center"
                >
                  <h3 className="font-semibold band-name-glow">{member.name}</h3>
                  <p className="text-sm text-gray-400">{member.role}</p>
                </div>
              ))}
            </div>
            
            {/* Member info - Mobile only */}
            <div className="mt-8 md:hidden">
              {/* Row 1: Ken & Eddie */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-black/50 border border-blue-900/30 p-4 rounded-lg text-center">
                  <h3 className="font-semibold text-lg band-name-glow">Ken Marut</h3>
                  <p className="text-gray-400">Banjo/Guitar</p>
                </div>
                <div className="bg-black/50 border border-blue-900/30 p-4 rounded-lg text-center">
                  <h3 className="font-semibold text-lg band-name-glow">Eddie Collins</h3>
                  <p className="text-gray-400">Bass/Banjo</p>
                </div>
              </div>
              
              {/* Row 2: Loren & Roam */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-black/50 border border-blue-900/30 p-4 rounded-lg text-center">
                  <h3 className="font-semibold text-lg band-name-glow">Loren Gunn</h3>
                  <p className="text-gray-400">Mando/Vox</p>
                </div>
                <div className="bg-black/50 border border-blue-900/30 p-4 rounded-lg text-center">
                  <h3 className="font-semibold text-lg band-name-glow">Roam Gunn</h3>
                  <p className="text-gray-400">Guitar/Vox</p>
                </div>
              </div>
              
              {/* Row 3: Jerry centered */}
              <div className="flex justify-center">
                <div className="bg-black/50 border border-blue-900/30 p-4 rounded-lg text-center max-w-xs w-full">
                  <h3 className="font-semibold text-lg band-name-glow">Jerry Hill</h3>
                  <p className="text-gray-400">Fiddle</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 relative z-10" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,4,40,0.8) 100%)' }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-center mb-12 uppercase text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>Keep In Touch</h2>
          
          {/* Bookings Section */}
          <div className="bg-black/50 p-8 rounded-lg mb-8 border border-blue-900/30 space-glow">
            <div className="space-y-4 text-lg">
              <p className="text-enhanced-gray-200">
                For bookings and inquiries:
              </p>
              <p>
                <a href="mailto:acrossthewaymusic@gmail.com" className="text-white text-xl hover:text-gray-300 transition underline">
                  acrossthewaymusic@gmail.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Padding for footer */}
      <div className="h-16"></div>
      
      {/* Footer with Social Icons - Fixed at bottom */}
      <footer className="fixed bottom-0 left-0 right-0 py-3 px-4 bg-black/95 backdrop-blur-sm border-t border-blue-900/30 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Copyright */}
          <div className="text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} Across The Way Music</p>
          </div>
          
          {/* Social Icons */}
          <div className="flex space-x-6">
            <a 
              href="https://www.instagram.com/acrossthewaymusic/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="group transition-all transform hover:scale-125"
              aria-label="Instagram"
            >
              <svg className="w-8 h-8 text-pink-600/70 group-hover:text-pink-500 group-hover:filter group-hover:drop-shadow-[0_0_20px_rgba(236,72,153,0.8)] transition-all" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z"/>
              </svg>
            </a>
            <a 
              href="https://www.youtube.com/@AcrossTheWayMusic" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="group transition-all transform hover:scale-125"
              aria-label="YouTube"
            >
              <svg className="w-8 h-8 text-red-600/70 group-hover:text-red-500 group-hover:filter group-hover:drop-shadow-[0_0_20px_rgba(239,68,68,0.8)] transition-all" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
          </div>
        </div>
      </footer>
      
      {/* Spacer for fixed footer */}
      <div className="h-16"></div>
      
      {/* Flyer Modal */}
      {expandedFlyer && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setExpandedFlyer(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img 
              src={expandedFlyer} 
              alt="Event flyer"
              className="w-full h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setExpandedFlyer(null)}
              className="absolute top-4 right-4 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      {/* Expanded Show Modal */}
      {expandedShow && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setExpandedShow(null)}
        >
          <div 
            className="relative max-w-4xl w-full show-card-enhanced rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setExpandedShow(null)}
              className="absolute top-4 right-4 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition z-10 focus:outline-none focus:ring-2 focus:ring-blue-400/50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="flex flex-col">
              {/* Photos Row - Event/Venue Left, Band Right */}
              <div className="flex">
                {/* Show Art - Left */}
                <div className="w-1/2 p-8 flex items-center justify-center bg-gradient-to-br from-blue-600/10 to-purple-600/10">
                  {expandedShow.flyerUrl ? (
                    <img
                      src={expandedShow.flyerUrl}
                      alt={`${expandedShow.venue.name} flyer`}
                      className="max-w-full max-h-[400px] object-contain rounded-lg shadow-2xl"
                    />
                  ) : (
                    <div className="w-full max-w-[350px] h-[350px]">
                      <DefaultShowArt size="large" />
                    </div>
                  )}
                </div>

                {/* Band Photo - Right */}
                <div className="w-1/2 p-8 flex items-center justify-center bg-gradient-to-br from-purple-600/10 to-pink-600/10">
                  {expandedShow.bandConfig && bandPhotos[expandedShow.bandConfig] ? (
                    <img
                      src={bandPhotos[expandedShow.bandConfig]}
                      alt={`${expandedShow.bandConfig === 'duet' ? 'Duet' : expandedShow.bandConfig === 'trio' ? 'Trio' : 'Full Band'}`}
                      className="max-w-full max-h-[400px] object-contain rounded-lg shadow-2xl"
                    />
                  ) : expandedShow.eventType?.includes('full') ? (
                    <img
                      src={bandPhotos['full']}
                      alt="Full Band"
                      className="max-w-full max-h-[400px] object-contain rounded-lg shadow-2xl"
                    />
                  ) : (
                    <div className="w-full max-w-[350px] h-[350px] flex items-center justify-center bg-gray-800/50 rounded-lg">
                      <div className="text-center">
                        <p className="text-4xl font-bold text-gray-400 mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                          {expandedShow.bandConfig === 'duet' ? 'DUET' :
                           expandedShow.bandConfig === 'trio' ? 'TRIO' :
                           'FULL BAND'}
                        </p>
                        <p className="text-gray-500">Performance</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Show Details - Below photos */}
              <div className="p-8 space-y-6">
                <div>
                  <h2 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    {expandedShow.venue.name}
                  </h2>
                  {expandedShow.notes && (
                    <p className="text-xl text-enhanced-gray-200">{expandedShow.notes}</p>
                  )}
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center text-lg">
                    <svg className="w-6 h-6 mr-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="font-semibold">{formatShowDate(expandedShow.date, 'EEEE, MMMM d, yyyy')}</span>
                  </div>
                  
                  <div className="flex items-center text-lg">
                    <svg className="w-6 h-6 mr-3 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-semibold">{expandedShow.startTime} {expandedShow.endTime && `- ${expandedShow.endTime}`}</span>
                  </div>
                  
                  {!expandedShow.isPrivate && (
                    expandedShow.eventType === 'internet' && expandedShow.streamUrl ? (
                      <div className="flex items-start text-lg">
                        <svg className="w-6 h-6 mr-3 mt-1 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        <div>
                          <a
                            href={expandedShow.streamUrl.startsWith('http') ? expandedShow.streamUrl : `https://${expandedShow.streamUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300"
                          >
                            Watch Online
                          </a>
                          <p className="text-sm text-gray-400">Click to join the stream</p>
                        </div>
                      </div>
                    ) : expandedShow.venue.address.street ? (
                      <div className="flex items-start text-lg">
                        <svg className="w-6 h-6 mr-3 mt-1 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <div>
                          <p>{expandedShow.venue.address.street}</p>
                          <p>{expandedShow.venue.address.city}, {expandedShow.venue.address.state} {expandedShow.venue.address.zip}</p>
                        </div>
                      </div>
                    ) : null
                  )}
                </div>
                
                {/* Event Type and Tickets */}
                <div className="space-y-4">
                  {(expandedShow.eventType === 'private' || expandedShow.isPrivate) && (
                    <p className="text-gray-500 font-semibold text-xl">Private Event</p>
                  )}
                  {expandedShow.eventType !== 'private' && !expandedShow.isPrivate && (
                    <p className="text-xl">
                      <span className="free-event-badge text-base">
                        {(expandedShow.eventType === 'internet' || expandedShow.venue?.name?.toLowerCase().includes('up late')) ? 'Online' :
                         (expandedShow.eventType === 'ticketed' || expandedShow.isTicketed) ? 'Ticketed Event' :
                         'Free Event'}
                      </span>
                    </p>
                  )}
                  {/* Band Configuration */}
                  {expandedShow.eventType === 'ticketed' && expandedShow.ticketInfo && (
                    <a 
                      href={expandedShow.ticketInfo.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn-primary text-lg"
                    >
                      <span className="mr-2">Get Tickets</span>
                      <span>{expandedShow.ticketInfo.price}</span>
                      <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )}
                </div>
                
                {/* Social Sharing / Private Event Booking */}
                <div className="pt-6 border-t border-blue-900/30">
                  {(expandedShow.eventType === 'private' || expandedShow.isPrivate) ? (
                    <>
                      <p className="text-gray-400 mb-4">Book us for your private event:</p>
                      <button
                        onClick={() => {
                          setExpandedShow(null);
                          setShowBookingForm(true);
                        }}
                        className="btn-primary"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Book Private Event
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-gray-400 mb-4">Share this event:</p>
                      <div className="flex space-x-4">
                        <a
                          href={`https://www.instagram.com/`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded hover:from-purple-700 hover:to-pink-700 transition"
                        >
                          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"/>
                          </svg>
                          I'll Be There!
                        </a>
                        <button
                          onClick={() => {
                            const eventDate = new Date(expandedShow.date);
                            const eventTitle = `Across The Way at ${expandedShow.venue.name}`;
                            const eventDetails = expandedShow.notes || 'Live music performance';
                            const location = expandedShow.isPrivate ? expandedShow.venue.name : `${expandedShow.venue.address.street}, ${expandedShow.venue.address.city}, ${expandedShow.venue.address.state} ${expandedShow.venue.address.zip}`;
                            
                            // Parse start time and create proper date objects
                            const [startHour, startMinute] = expandedShow.startTime.split(':').map(Number);
                            const startDateTime = new Date(eventDate);
                            startDateTime.setHours(startHour, startMinute, 0, 0);
                            
                            // If endTime exists, use it; otherwise add 2 hours to start time
                            let endDateTime = new Date(startDateTime);
                            if (expandedShow.endTime) {
                              const [endHour, endMinute] = expandedShow.endTime.split(':').map(Number);
                              endDateTime.setHours(endHour, endMinute, 0, 0);
                            } else {
                              endDateTime.setHours(startDateTime.getHours() + 2);
                            }
                            
                            // Format dates for Google Calendar (YYYYMMDDTHHmmssZ)
                            const formatCalendarDate = (date: Date) => {
                              return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
                            };
                            
                            // Create Google Calendar URL
                            const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventTitle)}&details=${encodeURIComponent(eventDetails)}&location=${encodeURIComponent(location)}&dates=${formatCalendarDate(startDateTime)}/${formatCalendarDate(endDateTime)}`;
                            
                            window.open(googleCalendarUrl, '_blank');
                          }}
                          className="flex items-center bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-600 transition"
                        >
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Add to Calendar
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Booking Form Modal */}
      <BookingForm isOpen={showBookingForm} onClose={() => setShowBookingForm(false)} />
      
      {/* Subscribe Popup */}
      {showSubscribePopup && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowSubscribePopup(false)}
        >
          <div 
            className="relative max-w-md w-full bg-gray-900 rounded-lg p-8 border border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowSubscribePopup(false)}
              className="absolute top-4 right-4 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <h2 className="text-3xl font-bold mb-4 text-center" style={{ fontFamily: 'Oswald, sans-serif' }}>
              Stay Connected
            </h2>
            <p className="text-gray-300 mb-6 text-center">
              Join our mailing list for updates on shows, new music, and exclusive content!
            </p>
            
            <form 
              onSubmit={async (e) => {
                e.preventDefault();
                
                // Rate limiting - prevent submissions within 30 seconds
                const now = Date.now();
                const timeSinceLastSubmission = now - lastSubmissionTime;
                if (timeSinceLastSubmission < 30000) {
                  const errorDiv = e.currentTarget.querySelector('.error-message') as HTMLDivElement;
                  if (errorDiv) {
                    errorDiv.textContent = `Please wait ${Math.ceil((30000 - timeSinceLastSubmission) / 1000)} seconds before subscribing again.`;
                    errorDiv.style.display = 'block';
                  }
                  return;
                }
                
                const formData = new FormData(e.currentTarget);
                const firstName = formData.get('firstName') as string;
                const lastName = formData.get('lastName') as string;
                const email = formData.get('email') as string;
                const honeypot = formData.get('website') as string;
                
                // Check honeypot field (bots will fill this, humans won't see it)
                if (honeypot) {
                  // Silently reject bot submissions
                  const submitButton = e.currentTarget.querySelector('button[type="submit"]') as HTMLButtonElement;
                  submitButton.classList.add('bg-green-600');
                  submitButton.textContent = '✓ Subscribed!';
                  setTimeout(() => {
                    setShowSubscribePopup(false);
                  }, 1500);
                  return;
                }
                
                // Show loading state
                const submitButton = e.currentTarget.querySelector('button[type="submit"]') as HTMLButtonElement;
                const originalText = submitButton.textContent;
                submitButton.disabled = true;
                submitButton.textContent = 'Subscribing...';
                
                // Clear any previous error
                const errorDiv = e.currentTarget.querySelector('.error-message') as HTMLDivElement;
                if (errorDiv) {
                  errorDiv.style.display = 'none';
                }
                
                try {
                  // First and last names are optional
                  
                  // Use correct API endpoint for local vs production
                  const apiUrl = process.env.NODE_ENV === 'development' 
                    ? 'http://localhost:7071/api/subscribe'
                    : '/api/subscribe';
                  
                  const response = await fetch(apiUrl, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      email,
                      firstName: firstName || undefined,
                      lastName: lastName || undefined
                    })
                  });
                  
                  const data = await response.json();
                  
                  if (response.ok) {
                    // Show success message
                    submitButton.classList.add('bg-green-600');
                    submitButton.textContent = '✓ Subscribed!';
                    setLastSubmissionTime(Date.now()); // Record successful submission time
                    
                    // Store in localStorage to persist across page refreshes
                    localStorage.setItem('lastNewsletterSubmission', Date.now().toString());
                    
                    setTimeout(() => {
                      setShowSubscribePopup(false);
                    }, 1500);
                  } else {
                    throw new Error(data.error || 'Failed to subscribe');
                  }
                } catch (error) {
                  // Show error message
                  if (errorDiv) {
                    errorDiv.textContent = error instanceof Error ? error.message : 'Failed to subscribe. Please try again.';
                    errorDiv.style.display = 'block';
                  }
                  submitButton.disabled = false;
                  submitButton.textContent = originalText || 'Subscribe';
                }
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-200 mb-2 uppercase tracking-wide">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all"
                    placeholder="John (optional)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-200 mb-2 uppercase tracking-wide">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all"
                    placeholder="Doe (optional)"
                  />
                </div>
              </div>
              
              {/* Honeypot field - hidden from users, traps bots */}
              <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-200 mb-2 uppercase tracking-wide">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:shadow-[0_0_10px_rgba(96,165,250,0.3)] transition-all"
                  placeholder="your@email.com"
                />
              </div>
              
              <div className="error-message text-red-500 text-sm" style={{ display: 'none' }}></div>
              
              <div className="flex flex-col gap-3 mt-6">
                <button
                  type="submit"
                  className="w-full btn-primary"
                >
                  Subscribe
                </button>
                <button
                  type="button"
                  onClick={() => setShowSubscribePopup(false)}
                  className="w-full px-6 py-2 text-gray-400 hover:text-white transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicSite;
